import React from 'react'
import { useTranslation } from 'react-i18next'
import { FALLBACK_IMAGE, applyFallbackIfNeeded } from '../../utils/imageFallback'
import RemotionPreview from './RemotionPreview'

/**
 * ProductProposalCard
 * Displays a product proposal in the approval queue
 *
 * Props:
 * - proposal: ProductProposal entity with proposal_id, preview_url, caption_text, status, etc.
 * - onSelect: Callback when card is clicked to open modal
 */

function formatDate(dateString) {
  if (!dateString) return '--'
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return '--'
  }
}

function formatPrice(price, currency = 'EUR') {
  if (!price) return '--'
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(price)
  } catch {
    return `${price} ${currency}`
  }
}

function ProductProposalCard({ proposal, onSelect }) {
  const { t } = useTranslation('approval')

  // Determine preview source
  const previewSrc = proposal.preview_url || proposal.sas_url || FALLBACK_IMAGE

  // Check status
  const isRendering = proposal.status === 'rendering_video'
  const isVideoReady = proposal.status === 'video_ready'

  // Get product info from template variables if available
  const productTitle = proposal.template_variables?.product_title || proposal.product_name || proposal.product_id
  const salePrice = proposal.template_variables?.sale_price

  return (
    <div
      onClick={() => !isRendering && onSelect(proposal)}
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-shadow duration-200 ${
        isRendering ? 'opacity-75 cursor-wait' : 'hover:shadow-lg cursor-pointer'
      }`}
    >
      {/* Preview Image (9:16 aspect ratio) */}
      <div className="aspect-story bg-gray-100 relative group">
        {/* VIDEO_READY: show video player with external URL */}
        {proposal.template_variables?.video_url ? (
          <video
            src={proposal.template_variables.video_url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
            onMouseEnter={(e) => e.target.play()}
            onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0 }}
          />
        ) : proposal.template_id && proposal.template_id !== 'existing_video' && proposal.template_variables ? (
          <RemotionPreview
            templateId={proposal.template_id}
            templateVariables={proposal.template_variables}
            initialFrame={120}
            controls={false}
            autoPlay={false}
            loop={false}
            className="w-full h-full"
            style={{ width: '100%', height: '100%' }}
          />
        ) : (
          <img
            src={previewSrc}
            alt={productTitle || t('page.grid.productProposalAlt', 'Product Proposal')}
            className="w-full h-full object-cover"
            onError={applyFallbackIfNeeded}
          />
        )}

        {/* Type Badge - Purple for E-Commerce (top-right) */}
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-600 text-white shadow">
            {t('page.grid.productTypeBadge', 'Produkt')}
          </span>
        </div>

        {/* Status Badge */}
        <div className="absolute bottom-2 left-2">
          {isRendering ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 shadow animate-pulse">
              <svg className="animate-spin -ml-1 mr-1.5 h-3 w-3 text-blue-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('page.grid.renderingVideoBadge', 'Rendering...')}
            </span>
          ) : isVideoReady ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 shadow">
              {t('page.grid.videoReadyBadge', 'Video Ready')}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 shadow">
              {t('page.grid.awaitingApprovalBadge', 'Awaiting Approval')}
            </span>
          )}
        </div>

        {/* Template indicator (bottom-right) */}
        {proposal.template_id && (
          <div className="absolute bottom-2 right-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-black/50 text-white">
              {proposal.template_id === 'existing_video' ? t('page.grid.userProvidedVideo', 'User provided Video') : proposal.template_id}
            </span>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4">
        {/* Product Title */}
        <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
          {productTitle}
        </p>

        {/* Price if available */}
        {salePrice && (
          <p className="text-lg font-bold text-purple-600 mb-2">
            {salePrice}
          </p>
        )}

        {/* Caption Preview */}
        {proposal.caption_text && (
          <p className="text-xs text-gray-600 line-clamp-2 mb-3">
            {proposal.caption_text}
          </p>
        )}

        {/* Metadata */}
        <div className="space-y-1 text-xs text-gray-500 mb-3">
          {proposal.scheduled_for && (
            <div className="flex justify-between">
              <span>{t('page.grid.scheduledFor', 'Scheduled')}:</span>
              <span className="font-semibold">{formatDate(proposal.scheduled_for)}</span>
            </div>
          )}
          {proposal.created_at && (
            <div className="flex justify-between">
              <span>{t('page.grid.createdAt', 'Created')}:</span>
              <span className="font-semibold">{formatDate(proposal.created_at)}</span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            if (!isRendering) onSelect(proposal)
          }}
          disabled={isRendering}
          className={`w-full py-2 text-sm rounded-lg transition-colors ${
            isRendering
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'btn-primary'
          }`}
        >
          {isRendering
            ? t('page.grid.renderingButton', 'Video wird gerendert...')
            : t('page.grid.reviewButton', 'Review')
          }
        </button>

        {/* Rendering progress message */}
        {isRendering && (
          <p className="mt-2 text-xs text-blue-600 text-center">
            {t('page.grid.renderingMessage', 'Das Video wird erstellt. Dies kann 2-3 Minuten dauern.')}
          </p>
        )}
      </div>
    </div>
  )
}

export default ProductProposalCard
